import { Moment } from 'moment';
import { EphemerisBodyKey, EphemerisBodyState, EphemerisChange, EphemerisTable } from './ephemeris.models';

/** Eén cel: `poort.lijn` van één lichaam in één rij. */
export interface EphemerisCellVm {
  readonly text: string;
  readonly retrograde: boolean;
  /** Gewijzigd op het moment van deze rij (lijnwissel of station). */
  readonly changed: boolean;
  /** Het lichaam keert op dit moment: naar retrograde of naar direct. */
  readonly station: 'retrograde' | 'direct' | null;
  /** Lengte (graden) bij deze rij, voor de tooltip; `undefined` als de API ze niet meegaf. */
  readonly longitude: number | undefined;
}

export interface EphemerisRowVm {
  /** Stabiele track-sleutel (UTC-epoch in ms). */
  readonly id: number;
  readonly atUtc: Moment;
  readonly cells: readonly EphemerisCellVm[];
}

export interface EphemerisRowsVm {
  /** Zichtbare kolommen in tabelvolgorde. */
  readonly columns: readonly EphemerisBodyKey[];
  /** De stand bij het vensterbegin (beginrij). */
  readonly initialCells: readonly EphemerisCellVm[];
  readonly rows: readonly EphemerisRowVm[];
}

/**
 * Bouwt de cellen van de wissels:
 * - rolt de stand door (elke rij toont de volledige stand van alle zichtbare lichamen);
 * - markeert gewijzigde cellen (en stations);
 * - laat rijen weg waarin alleen verborgen lichamen wijzigen (de stand rolt wel door).
 *
 * Tijdzone, dagkoppen en de rij die nu geldt komen erbij in {@link buildEphemerisDisplay}
 * (samen met de ingevoegde rijen).
 */
export function buildEphemerisRows(table: EphemerisTable, hidden: ReadonlySet<EphemerisBodyKey>): EphemerisRowsVm {
  const columnIndexes = table.bodies
    .map((key, index) => ({ key, index }))
    .filter(column => !hidden.has(column.key));
  const columns = columnIndexes.map(column => column.key);

  const states: EphemerisBodyState[] = [...table.initial];
  const initialCells = columnIndexes.map(column =>
    toCell(states[column.index], undefined, table.initialLongitudes?.[column.index]),
  );

  const rows: EphemerisRowVm[] = [];
  for (const row of table.rows) {
    const changesByBody = new Map<number, EphemerisChange>();
    for (const change of row.changes) {
      states[change.body] = { gate: change.gate, line: change.line, retrograde: change.retrograde };
      changesByBody.set(change.body, change);
    }

    if (!columnIndexes.some(column => changesByBody.has(column.index))) {
      continue;
    }

    rows.push({
      id: row.atUtc.valueOf(),
      atUtc: row.atUtc,
      cells: columnIndexes.map(column =>
        toCell(states[column.index], changesByBody.get(column.index), row.longitudes?.[column.index]),
      ),
    });
  }

  return { columns, initialCells, rows };
}

function toCell(state: EphemerisBodyState, change: EphemerisChange | undefined, longitude: number | undefined): EphemerisCellVm {
  return {
    text: `${state.gate}.${state.line}`,
    retrograde: state.retrograde,
    changed: change !== undefined,
    station: change?.kind === 'station' ? (change.retrograde ? 'retrograde' : 'direct') : null,
    longitude,
  };
}
